// Project checklist — the pre-construction / materials checklist an admin
// walks per job (2026-09-23, Renan). One list per project; every row is a
// fact about THIS job: is the item in scope, what exactly was specified, who
// supplies it, and whether it is done.
//
// The template below is the masonry pattern Renan runs today — the
// 145 E Columbia spreadsheet: 9 sections, 32 items, each with the guidance
// text that used to sit in a hidden column. Other trade packs have no
// template yet: their checklist starts empty and the admin adds rows. A
// template is only ever a STARTING POINT — the rows are copied into the job
// the first time its checklist is opened and edited freely from then on, so
// changing the template never rewrites a job already on paper.
//
// Pure data + pure functions. No DOM, no network.

// 2026-09-23 (Renan): 'ready' = lined up but not finished (ordered / on site /
// confirmed). Green like Done, one shade lighter; Pending is the red one.
export const STATUSES = Object.freeze(['pending', 'ready', 'done']);
export const INCLUDED = Object.freeze(['', 'yes', 'no']);

const S = (section, items) => ({ section, items: items.map(([item, hint]) => ({ item, hint })) });

export const TEMPLATES = Object.freeze({
  masonry: [
    S('1. Drawings & submittals', [
      ['Masonry shop drawings', 'Elevations, coursing, bond pattern, control joints, sills & lintels.'],
      ['Engineered shop drawings (sealed)', 'Ties / anchors, shelf angles / Fero brackets, loose lintel schedule. Letters of Assurance if required.'],
      ['Product data & samples', 'Data sheets: brick, mortar, ties, membrane, insulation, flashing, vents. Samples: brick, mortar colour, sills.'],
      ['Mock-up panel', 'Size & location. Includes ties, flashing, weeps & insulation. Approved before ordering.'],
    ]),
    S('2. Backup wall & membrane', [
      ['Backup wall type', 'Concrete / CMU / steel stud / wood. Drives tie type, fasteners & spacing.'],
      ['Air / vapour barrier', 'Blueskin SA 36" x 75\' (or VP100 / fluid-applied). Primer: Aquatac 5 gal. Our scope or by others?'],
      ['Membrane details', 'Jamb strips SA 6", head SA 12", 6x6 patches over L-plates (concrete backup), Polybitume sealant.'],
      ['Window / door interface', 'Windows in before brick? Who does the membrane tie-in? Sill & head heights.'],
    ]),
    S('3. Insulation', [
      ['Insulation', 'Type (Rockwool Cavityrock / XPS / polyiso), thickness (2" / 3" / 4"), R-value, board size (24x48), fastening.'],
      ['Cavity & fire stops', 'Air space 1" min clear. Total cavity sets tie length. Fire stops at floor lines if required.'],
    ]),
    S('4. Ties & reinforcement', [
      ['Ties', 'System (L-plate + V-tie / Fero / H&B / Blok-Lok), material (SS 304 / 316 or hot-dip galv), size (L-plate 105mm + V-tie 80mm), spacing (concrete / CMU 32" x 24", stud 16" x 24").'],
      ['Tie fasteners', 'Concrete: Kwik-HUS 1/4 x 3. CMU: 1/4 x 2-1/2. Steel stud: 2 x #10-16 per tie. Seismic wire if required.'],
      ['CMU reinforcement', 'Joint reinforcement (ladder / truss, 9 ga, galv / SS), rebar size & spacing, dowels, grouted cells.'],
    ]),
    S('5. Masonry units', [
      ['Brick', 'Type (clay / concrete / thin), manufacturer, colour & texture, size (modular / Norman / 4x16), joint 3/8" or 1/2". Same lot for the whole project.'],
      ['Corners & special shapes', 'L-corners (every course / every other), soldiers, rowlocks, bullnose, sill brick.'],
      ['CMU', 'Size (10 / 15 / 20 cm), finish (standard / split / ground face), bond beam, lintel, knockout.'],
      ['Stone / cast stone', 'Type, colour, sizes, anchors (SS), qty per piece.'],
      ['Precast sills, copings & caps', 'Profile, length, colour, drip.'],
    ]),
    S('6. Mortar & grout', [
      ['Mortar', 'Type N / S, colour (match mock-up), bulk bag / silo / bagged. Approx. 450 sf of wall per bulk bag.'],
      ['Grout', 'Fine / coarse, strength, pump or bucket.'],
      ['Cleaning', 'Admixture, heaters, tarps below 5 C. Cleaning product approved by brick manufacturer.'],
    ]),
    S('7. Flashing, drainage & joints', [
      ['Through-wall flashing', 'Blueskin TWF 18" x 75\'. At base, shelf angles, heads & sills. End dams.'],
      ['Metal drip edge', 'Stainless / prefinished, gauge, colour. 1/2" past brick face.'],
      ['Base drainage & vents', 'Mortar break / mortar net. Blok-Lok cell vents (colour) at 24".'],
      ['Control joints & sealant', 'Locations, width, soft joint under shelf angles. Sealant & backer rod if in scope.'],
    ]),
    S('8. Steel', [
      ['Shelf angles & Fero brackets', 'L4x4x1/4 hot-dip galv. Fero Fast Thermal Bracket at 30", 1 anchor per bracket.'],
      ['Loose lintels', 'Up to 9\': L4x4x1/4. 9\'-12\': L5x3.5x1/4. Over 12\': per engineer. 8" bearing each side. Galv / primed.'],
    ]),
    S('9. Site & execution', [
      ['Backup wall ready', 'Membrane done & inspected. Walls plumb. Openings framed.'],
      ['Layout & coursing', 'Story pole. Coursing vs windows, doors & floor lines.'],
      ['Access & equipment', 'Scaffold / mast climber (engineered, tagged), forklift, mixer, saw & blades.'],
      ['Laydown & winter protection', 'Storage area agreed with GC, material covered & off the ground. Tarps, hoarding, heaters.'],
      ['Inspections & deliveries', 'Engineer tie inspection, rebar before grout, testing. Delivery order: membrane & insulation, ties, brick & mortar, sills.'],
    ]),
  ],
});

/** The rows a job starts with for its trade pack — flat, in order, ready to
 *  insert. No template for the pack → an empty list, never an error: the
 *  admin builds the list by hand. */
export function templateRows(packKey) {
  const t = TEMPLATES[packKey] || [];
  const rows = [];
  t.forEach(sec => sec.items.forEach(it => rows.push({
    section: sec.section, item: it.item, hint: it.hint,
    sort_order: (rows.length + 1) * 10,
  })));
  return rows;
}

/** Group a project's rows by section, in sort order. Section order is the
 *  order of each section's first row, so a hand-added section lands last. */
export function groupBySection(rows) {
  const sorted = (rows || []).slice().sort((a, b) =>
    (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
  const out = [];
  const idx = {};
  sorted.forEach(r => {
    const key = r.section || '';
    if (idx[key] == null) { idx[key] = out.length; out.push({ section: key, items: [] }); }
    out[idx[key]].items.push(r);
  });
  return out;
}

/** How far the job is: rows marked "no" (not in scope) count as neither
 *  pending nor done — they are not work. */
export function progress(rows) {
  let done = 0, ready = 0, total = 0;
  (rows || []).forEach(r => {
    if (r.included === 'no') return;
    total++;
    if (r.status === 'done') done++;
    else if (r.status === 'ready') ready++;
  });
  return { done, ready, total, pct: total ? Math.round(done / total * 100) : 0,
           readyPct: total ? Math.round(ready / total * 100) : 0 };
}
